/**
 * useElementContentHeight.ts
 * BUG-2026-0194 — Measure the CONTENT height (clientHeight minus vertical
 * padding) of a container, kept current through a ResizeObserver.
 *
 * The Master Gantt sizes its Highcharts chart to the space the page gives it
 * (rows scroll inside the chart, navigator pinned at the bottom), so it needs
 * the live pixel height of its flex container rather than a row-count formula.
 *
 * Returns 0 until the first layout measurement — callers should treat 0 as
 * "not measured yet" and hold off mounting size-sensitive children.
 */

import { useLayoutEffect, useRef, useState, type RefObject } from 'react';

export function useElementContentHeight<T extends HTMLElement>(): [RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = (): void => {
      const style = window.getComputedStyle(el);
      const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
      const next = Math.max(0, Math.floor(el.clientHeight - padding));
      setHeight((prev) => (prev === next ? prev : next));
    };

    measure();
    if (typeof ResizeObserver === 'undefined') return; // jsdom / very old browsers: single measure
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, height];
}
